import { useCallback, useSyncExternalStore } from "react";

export type Appearance = "light" | "dark" | "system";

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const applyTheme = (appearance: Appearance) => {
  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark());

  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
};

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.matchMedia("(prefers-color-scheme: dark)");
};

const handleSystemThemeChange = () => {
  const currentAppearance = localStorage.getItem("appearance") as Appearance;
  applyTheme(currentAppearance ?? "system");
};

export function initializeTheme() {
  const savedAppearance =
    (localStorage.getItem("appearance") as Appearance) || "system";

  applyTheme(savedAppearance);

  mediaQuery()?.addEventListener("change", handleSystemThemeChange);
}

const readAppearance = (): Appearance => {
  const stored = localStorage.getItem("appearance");
  return stored === "light" || stored === "dark" ? stored : "system";
};

const subscribeAppearance = (listener: () => void) => {
  window.addEventListener("storage", listener);
  window.addEventListener("appearance-change", listener);
  return () => {
    window.removeEventListener("storage", listener);
    window.removeEventListener("appearance-change", listener);
  };
};

export function useAppearance() {
  const appearance = useSyncExternalStore(
    subscribeAppearance,
    readAppearance,
    () => "system" as Appearance,
  );
  const updateAppearance = useCallback((mode: Appearance) => {
    if (mode === "system") {
      localStorage.removeItem("appearance");
    } else {
      localStorage.setItem("appearance", mode);
    }
    applyTheme(mode);
    window.dispatchEvent(new Event("appearance-change"));
  }, []);

  return { appearance, updateAppearance } as const;
}
